import { EXPECTED_ROLE } from "@/constants/role";
import { HttpStatus } from "@/enum";
import { clearToastQueue } from "@/pages/OrderByPOS/Component/Message/ShowToast";
import { postService } from "@/services/apiService.ts";
import { getBusinesseById } from "@/services/ServiceApi";
import { hasAppAccess } from "@/utils/appAccess";
import { getUser, removeShop, removeToken, removeUser, setShop, setToken, setUser } from "@/utils/auth.ts";
import socket from "@/utils/socket";
import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import Toastify from "toastify-js";
import { RootState } from "./store";

interface AuthState {
    isAuthenticated: boolean;
    user: any | null;
    loading: boolean;
    error: string | null;
}

const initialState: AuthState = {
    isAuthenticated: !!localStorage.getItem('token'),
    user: getUser(),
    loading: false,
    error: null,
};

// Async thunk for login API call
export const loginUser = createAsyncThunk(
    'auth/login',
    async (credentials: { username: string; password: string }, { rejectWithValue }) => {
        try {
            const response: any = await postService({
                url: '/admin/authentication/login',
                data: credentials,
                headers: {
                    'Content-Type': 'application/json',
                    'x-api-key': import.meta.env.VITE_API_KEY,
                },
            });
            if (response.statusCode === HttpStatus.OK) {
                const userData = response.data?.user ?? response.data;
                const token = userData?.loginData?.token;
                const business = userData?.businesses?.[0];

                setToken(token);
                setUser(userData);
                if (business) setShop(business);

                return userData;
            }
            if (response.statusCode === HttpStatus.UNAUTHORIZED || response.statusCode === HttpStatus.FORBIDDEN) {
                return rejectWithValue(response);
            }
        } catch (error: any) {
            return rejectWithValue(error?.response?.data ?? { message: error?.message ?? "Login failed" });
        }
    }
);

// Async thunk for verifying a token handed off by the login portal
export const verifyPortalToken = createAsyncThunk(
    'auth/verifyPortalToken',
    async (token: string, { rejectWithValue }) => {
        try {
            const verifyResponse: any = await postService({
                url: "admin/authentication/token-verification",
                data: { token },
            });

            if (verifyResponse.statusCode !== HttpStatus.OK) {
                return rejectWithValue(verifyResponse);
            }

            const userData = verifyResponse.data?.user;
            if (!userData) {
                return rejectWithValue({ statusCode: HttpStatus.UNAUTHORIZED });
            }

            // Re-store whatever token this response reflects as current — the
            // backend's single-active-session enforcement can supersede the
            // token during verification, so keeping the URL-param token here
            // would leave us holding one that's already stale for the next switch.
            setToken(userData?.loginData?.token ?? token);

            const role = userData?.role;

            const hasRoleNameMatch = role?.name?.toLowerCase() === EXPECTED_ROLE;
            const hasAppAccessGrant = hasAppAccess(role?.permissions, "waiter-app");

            if (!hasRoleNameMatch && !hasAppAccessGrant) {
                removeToken();
                return rejectWithValue({ statusCode: HttpStatus.FORBIDDEN });
            }

            const businessId = userData?.businessIds?.[0];
            if (businessId) {
                const businessResponse: any = await getBusinesseById(businessId);
                if (businessResponse?.statusCode === HttpStatus.OK) {
                    setShop(businessResponse.data);
                }
            }

            const mergedUser = { ...userData, role };
            setUser(mergedUser);
            return mergedUser;
        } catch (error: any) {
            removeToken();
            return rejectWithValue(
                error?.response?.data ?? { statusCode: HttpStatus.UNAUTHORIZED },
            );
        }
    }
);

const authSlice = createSlice({
    name: "auth",
    initialState,
    reducers: {
        logout: (state) => {
            removeToken()
            removeUser()
            removeShop()
            state.isAuthenticated = false;
            state.user = null;
            state.error = null;
            clearToastQueue();
            socket.disconnect();
            sessionStorage.setItem("hasReloadedAfterLogin", "");
            window.location.href = import.meta.env.VITE_LOGIN_URL || "/login";
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(loginUser.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(loginUser.fulfilled, (state, action: PayloadAction<any>) => {
                state.loading = false;
                state.isAuthenticated = true;
                state.user = action.payload;
            })
            .addCase(loginUser.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;

                // @ts-ignore
                if (action.payload?.statusCode === HttpStatus.UNAUTHORIZED || action.payload?.statusCode === HttpStatus.FORBIDDEN) {
                    const successEl = document
                        .querySelectorAll("#failed-notification-content")[0]
                        .cloneNode(true) as HTMLElement;
                    successEl.classList.remove("hidden");
                    Toastify({
                        node: successEl,
                        duration: 1500,
                        newWindow: true,
                        close: true,
                        gravity: "bottom",
                        position: "left",
                        stopOnFocus: true,
                    }).showToast();
                }
            })
            .addCase(verifyPortalToken.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(verifyPortalToken.fulfilled, (state, action: PayloadAction<any>) => {
                state.loading = false;
                state.isAuthenticated = true;
                state.user = action.payload;
            })
            .addCase(verifyPortalToken.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
            });
    },
});

export const { logout } = authSlice.actions;
export const selectAuth = (state: RootState) => {
    if (localStorage.getItem("token") === null) {
        localStorage.setItem("token", "");
    }
    return state.auth.isAuthenticated;
}

export default authSlice.reducer;